import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Check } from "lucide-react";
import { toISO } from "./AdminWidgets";
import { euro } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detailText = (d) => (Array.isArray(d) ? d.map((e) => e.msg).join(" ") : d);

const field = "w-full rounded-xl border border-[rgba(11,31,58,0.15)] bg-[#F7F2EC] px-4 py-3 text-sm outline-none focus:border-[#0B1F3A] transition-colors placeholder:text-[#9A8F90]";

export const NewBookingModal = ({ auth, onClose, onCreated }) => {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState(toISO(new Date()));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) return;
    setTime("");
    axios.get(`${API}/slots`, { params: { date } }).then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  }, [date]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const total = services.filter((s) => selected.includes(s.id)).reduce((a, s) => a + s.price, 0);
  const canSubmit = selected.length > 0 && date && time && name.trim().length >= 2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/bookings`, { service_ids: selected, date, time, name, phone, email: email || null, note }, auth());
      toast.success(`Appuntamento aggiunto: ${r.data.name} · ${r.data.date} ${r.data.time}`);
      onCreated(r.data);
      onClose();
    } catch (err) {
      toast.error(detailText(err.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#0B1F3A]/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-booking-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 sm:p-8 shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#6F6466]">Agenda</p>
            <h2 className="font-display text-2xl font-bold text-[#0B1F3A]">Nuovo appuntamento</h2>
          </div>
          <button type="button" data-testid="new-booking-close" onClick={onClose} aria-label="Chiudi" className="rounded-full p-2 hover:bg-[#F7F2EC]"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-xs uppercase tracking-[0.2em] text-[#6F6466] mb-2">Servizi</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
          {services.map((s) => {
            const on = selected.includes(s.id);
            return (
              <button key={s.id} type="button" data-testid={`new-booking-service-${s.id}`} onClick={() => toggle(s.id)}
                className={`rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-[#0B1F3A] bg-[#0B1F3A] text-white" : "border-[rgba(11,31,58,0.15)] hover:border-[#0B1F3A]"}`}>
                <span className="flex items-center justify-between gap-2"><span className="truncate">{s.name}</span>{on && <Check className="w-4 h-4 shrink-0" />}</span>
                <span className={`text-xs ${on ? "text-white/70" : "text-[#6F6466]"}`}>€{s.price} · {s.duration} min</span>
              </button>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6F6466] mb-2">Data</p>
            <input data-testid="new-booking-date" type="date" value={date} min={toISO(new Date())} onChange={(e) => setDate(e.target.value)} className={field} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6F6466] mb-2">Orario</p>
            <select data-testid="new-booking-time" value={time} onChange={(e) => setTime(e.target.value)} className={field}>
              <option value="">Scegli…</option>
              {slots.map((s) => (
                <option key={s.time} value={s.time} disabled={s.remaining <= 0}>{s.time} · {s.remaining} posti</option>
              ))}
            </select>
            {slots.length === 0 && <p className="text-xs text-[#6F6466] mt-1">Nessun orario (chiuso o giornata passata).</p>}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <input data-testid="new-booking-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome cliente *" className={field} />
          <input data-testid="new-booking-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono (facoltativo)" className={field} />
          <input data-testid="new-booking-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (facoltativa, invia conferma)" className={field} />
          <input data-testid="new-booking-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (es. preso al telefono)" className={field} />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-[rgba(11,31,58,0.1)]">
          <p data-testid="new-booking-total" className="text-sm text-[#6F6466]">Totale <span className="font-display font-bold text-xl text-[#0B1F3A]">€{euro(total)}</span></p>
          <button type="submit" data-testid="new-booking-submit" disabled={!canSubmit}
            className="rounded-full bg-[#0B1F3A] text-white px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] disabled:opacity-40 hover:bg-[#071528] transition-colors inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Aggiungi
          </button>
        </div>
      </form>
    </div>
  );
};
